/**
 * Advanced Search Engine
 * Intelligent search with fuzzy matching, semantic analysis, and relevance scoring
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);



/**
 * Advanced Search Engine with Fuzzy Logic
 */
export const AdvancedSearchEngine = {
    // Configuration
    config: {
        fuzzyThreshold: 0.6,
        maxSuggestions: 10,
        semanticWeight: 0.3,
        fuzzyWeight: 0.4,
        exactWeight: 0.3,
        stemming: true,
        stopWords: ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'от', 'в', 'на', 'для', 'с', 'по', 'из', 'к', 'о']
    },

    // Search cache for performance
    cache: new Map(),
    
    // Statistics
    stats: {
        searches: 0,
        cacheHits: 0,
        averageTime: 0,
        topQueries: new Map()
    },

    /**
     * Initialize the advanced search engine
     */
    initialize() {
        this.loadSearchHistory();
        return this;
    },

    /**
     * Perform advanced search with fuzzy logic
     * @param {string} query - Search query
     * @param {Array} dataset - Data to search in
     * @param {Object} options - Search options
     * @returns {Array} Search results with relevance scores
     */
    search(query, dataset, options = {}) {
        const startTime = performance.now();
        
        // Update statistics
        this.stats.searches++;
        this.updateQueryStats(query);

        // Check cache first
        const cacheKey = this.getCacheKey(query, options);
        if (this.cache.has(cacheKey)) {
            this.stats.cacheHits++;
            if (debugMode === '1') {
                console.log('[AdvancedSearchEngine] Cache hit for:', query);
            }
            return this.cache.get(cacheKey);
        }

        // Prepare query
        const processedQuery = this.preprocessQuery(query);
        
        // Perform search
        const results = this.performAdvancedSearch(processedQuery, dataset, options);
        
        // Cache results
        this.cache.set(cacheKey, results);
        
        // Update timing statistics
        const searchTime = performance.now() - startTime;
        this.updateTimingStats(searchTime);
        
        console.log(`[AdvancedSearchEngine] Search completed in ${searchTime.toFixed(2)}ms`);
        return results;
    },

    /**
     * Preprocess search query
     * @param {string} query - Raw search query
     * @returns {Object} Processed query data
     */
    preprocessQuery(query) {
        const normalized = query.toLowerCase().trim();
        const tokens = this.tokenize(normalized);
        const stemmed = this.config.stemming ? this.stemTokens(tokens) : tokens;
        const filtered = this.removeStopWords(stemmed);
        
        return {
            original: query,
            normalized,
            tokens,
            stemmed,
            filtered,
            keywords: filtered.join(' ')
        };
    },

    /**
     * Tokenize text into words
     * @param {string} text - Text to tokenize
     * @returns {Array} Array of tokens
     */
    tokenize(text) {
        // Unicode-aware: keeps EN/RU/DE/FR/ES diacritics intact.
        return text.match(/[\p{L}\p{N}]+/gu) || [];
    },

    /**
     * Remove stop words from tokens
     * @param {Array} tokens - Array of tokens
     * @returns {Array} Filtered tokens
     */
    removeStopWords(tokens) {
        return tokens.filter(token => !this.config.stopWords.includes(token));
    },

    /**
     * Simple stemming for common endings
     * @param {Array} tokens - Array of tokens
     * @returns {Array} Stemmed tokens
     */
    stemTokens(tokens) {
        return tokens.map(token => {
            // English stemming
            if (token.endsWith('ing') && token.length > 5) {
                return token.slice(0, -3);
            }
            if (token.endsWith('ed') && token.length > 4) {
                return token.slice(0, -2);
            }
            if (token.endsWith('s') && token.length > 3) {
                return token.slice(0, -1);
            }
            
            // Russian stemming (basic)
            if (token.endsWith('ые') || token.endsWith('ий') || token.endsWith('ая')) {
                return token.slice(0, -2);
            }
            if (token.endsWith('ого') || token.endsWith('его')) {
                return token.slice(0, -3);
            }
            
            return token;
        });
    },

    /**
     * Perform the actual advanced search
     * @param {Object} processedQuery - Preprocessed query
     * @param {Array} dataset - Data to search
     * @param {Object} options - Search options
     * @returns {Array} Search results with scores
     */
    performAdvancedSearch(processedQuery, dataset, options) {
        const results = [];
        
        for (const item of dataset) {
            const score = this.calculateRelevanceScore(processedQuery, item, options);
            
            if (score > 0) {
                results.push({
                    item,
                    score,
                    matches: this.getMatchDetails(processedQuery, item)
                });
            }
        }
        
        // Sort by relevance score (descending)
        results.sort((a, b) => b.score - a.score);
        
        return results;
    },

    /**
     * Calculate relevance score for an item
     * @param {Object} processedQuery - Processed query
     * @param {Object} item - Item to score
     * @param {Object} options - Scoring options
     * @returns {number} Relevance score (0-1)
     */
    calculateRelevanceScore(processedQuery, item, options) {
        const text = this.getSearchableText(item, options);
        const normalizedText = text.toLowerCase();
        
        let totalScore = 0;
        
        // Exact match scoring
        const exactScore = this.getExactMatchScore(processedQuery.normalized, normalizedText);
        totalScore += exactScore * this.config.exactWeight;
        
        // Fuzzy match scoring
        const fuzzyScore = this.getFuzzyMatchScore(processedQuery.filtered, normalizedText);
        totalScore += fuzzyScore * this.config.fuzzyWeight;
        
        // Semantic scoring (keyword proximity, phrase matching)
        const semanticScore = this.getSemanticScore(processedQuery, normalizedText);
        totalScore += semanticScore * this.config.semanticWeight;
        
        return Math.min(totalScore, 1); // Cap at 1.0
    },

    /**
     * Get exact match score
     * @param {string} query - Normalized query
     * @param {string} text - Text to search in
     * @returns {number} Exact match score
     */
    getExactMatchScore(query, text) {
        if (text.includes(query)) {
            // Bonus for exact phrase match
            const position = text.indexOf(query);
            const positionBonus = position === 0 ? 0.2 : 0.1; // Bonus for starting match
            return 1.0 + positionBonus;
        }
        return 0;
    },

    /**
     * Get fuzzy match score using Levenshtein distance
     * @param {Array} keywords - Query keywords
     * @param {string} text - Text to search in
     * @returns {number} Fuzzy match score
     */
    getFuzzyMatchScore(keywords, text) {
        if (keywords.length === 0) return 0;
        
        let totalScore = 0;
        const textTokens = this.tokenize(text);
        
        for (const keyword of keywords) {
            let bestMatch = 0;
            
            for (const token of textTokens) {
                const similarity = this.calculateSimilarity(keyword, token);
                if (similarity > this.config.fuzzyThreshold) {
                    bestMatch = Math.max(bestMatch, similarity);
                }
            }
            
            totalScore += bestMatch;
        }
        
        return totalScore / keywords.length;
    },

    /**
     * Calculate semantic score based on keyword proximity and context
     * @param {Object} processedQuery - Processed query
     * @param {string} text - Text to analyze
     * @returns {number} Semantic score
     */
    getSemanticScore(processedQuery, text) {
        if (processedQuery.filtered.length < 2) return 0;
        
        const textTokens = this.tokenize(text);
        let proximityScore = 0;
        let matches = 0;
        
        // Find keyword positions
        const positions = new Map();
        for (let i = 0; i < textTokens.length; i++) {
            const token = textTokens[i];
            for (const keyword of processedQuery.filtered) {
                if (this.calculateSimilarity(keyword, token) > this.config.fuzzyThreshold) {
                    if (!positions.has(keyword)) {
                        positions.set(keyword, []);
                    }
                    positions.set(keyword, [...positions.get(keyword), i]);
                    matches++;
                }
            }
        }
        
        // Calculate proximity bonus
        if (positions.size > 1) {
            const keywordPositions = Array.from(positions.values());
            for (let i = 0; i < keywordPositions.length - 1; i++) {
                for (let j = i + 1; j < keywordPositions.length; j++) {
                    const minDistance = this.getMinDistance(keywordPositions[i], keywordPositions[j]);
                    if (minDistance < 10) { // Words within 10 positions
                        proximityScore += 1 / (minDistance + 1);
                    }
                }
            }
        }
        
        return (matches / processedQuery.filtered.length) * 0.7 + (proximityScore * 0.3);
    },

    /**
     * Calculate string similarity using Levenshtein distance
     * @param {string} str1 - First string
     * @param {string} str2 - Second string
     * @returns {number} Similarity score (0-1)
     */
    calculateSimilarity(str1, str2) {
        const maxLength = Math.max(str1.length, str2.length);
        if (maxLength === 0) return 1;
        
        const distance = this.levenshteinDistance(str1, str2);
        return 1 - (distance / maxLength);
    },

    /**
     * Calculate Levenshtein distance between two strings
     * @param {string} str1 - First string
     * @param {string} str2 - Second string
     * @returns {number} Edit distance
     */
    levenshteinDistance(str1, str2) {
        const matrix = [];
        
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        return matrix[str2.length][str1.length];
    },

    /**
     * Get minimum distance between two arrays of positions
     * @param {Array} positions1 - First array of positions
     * @param {Array} positions2 - Second array of positions
     * @returns {number} Minimum distance
     */
    getMinDistance(positions1, positions2) {
        let minDistance = Infinity;
        
        for (const pos1 of positions1) {
            for (const pos2 of positions2) {
                minDistance = Math.min(minDistance, Math.abs(pos1 - pos2));
            }
        }
        
        return minDistance;
    },

    /**
     * Extract searchable text from item
     * @param {Object} item - Item to extract text from
     * @param {Object} options - Extraction options
     * @returns {string} Searchable text
     */
    getSearchableText(item, options) {
        if (typeof item === 'string') return item;
        
        const fields = options.searchFields || ['name', 'title', 'content', 'description', 'n'];
        let text = '';
        
        for (const field of fields) {
            if (item[field]) {
                text += ' ' + item[field];
            }
        }
        
        return text;
    },

    /**
     * Get match details for highlighting
     * @param {Object} processedQuery - Processed query
     * @param {Object} item - Matched item
     * @returns {Object} Match details
     */
    getMatchDetails(processedQuery, item) {
        // This would be used for highlighting matched terms
        return {
            exactMatches: [],
            fuzzyMatches: [],
            keywords: processedQuery.filtered
        };
    },

    /**
     * Generate cache key for query and options
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @returns {string} Cache key
     */
    getCacheKey(query, options) {
        return `${query}|${JSON.stringify(options)}`;
    },

    /**
     * Update query statistics
     * @param {string} query - Search query
     */
    updateQueryStats(query) {
        const normalized = query.toLowerCase().trim();
        const count = this.stats.topQueries.get(normalized) || 0;
        this.stats.topQueries.set(normalized, count + 1);
    },

    /**
     * Update timing statistics
     * @param {number} searchTime - Search time in milliseconds
     */
    updateTimingStats(searchTime) {
        const currentAvg = this.stats.averageTime;
        const searches = this.stats.searches;
        this.stats.averageTime = ((currentAvg * (searches - 1)) + searchTime) / searches;
    },

    /**
     * Load search history from localStorage
     */
    loadSearchHistory() {
        try {
            const history = localStorage.getItem('advancedSearchHistory');
            if (history) {
                const data = JSON.parse(history);
                this.stats.topQueries = new Map(data.topQueries || []);
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[AdvancedSearchEngine] Failed to load search history:', error);
            }
        }
    },

    /**
     * Save search history to localStorage
     */
    saveSearchHistory() {
        try {
            const data = {
                topQueries: Array.from(this.stats.topQueries.entries()),
                lastSaved: Date.now()
            };
            localStorage.setItem('advancedSearchHistory', JSON.stringify(data));
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[AdvancedSearchEngine] Failed to save search history:', error);
            }
        }
    },

    /**
     * Get search statistics
     * @returns {Object} Search statistics
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.cache.size,
            topQueries: Array.from(this.stats.topQueries.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
        };
    },

    /**
     * Clear cache and reset statistics
     */
    clearCache() {
        this.cache.clear();
        if (debugMode === '1') {
                console.log('[AdvancedSearchEngine] Cache cleared');
            }
    },

    /**
     * Generate search suggestions based on query
     * @param {string} query - Partial query
     * @param {Array} dataset - Data for suggestions
     * @returns {Array} Search suggestions
     */
    getSuggestions(query, dataset) {
        if (query.length < 2) return [];
        
        const suggestions = new Set();
        const processedQuery = this.preprocessQuery(query);
        
        // Add popular queries that match
        for (const [popularQuery, count] of this.stats.topQueries) {
            if (popularQuery.includes(query.toLowerCase()) && popularQuery !== query.toLowerCase()) {
                suggestions.add({
                    text: popularQuery,
                    type: 'popular',
                    weight: count
                });
            }
        }
        
        // Add fuzzy matches from dataset
        const sampleSize = Math.min(dataset.length, 1000); // Limit for performance
        for (let i = 0; i < sampleSize; i++) {
            const item = dataset[i];
            const text = this.getSearchableText(item);
            const tokens = this.tokenize(text.toLowerCase());
            
            for (const token of tokens) {
                if (token.length > 2 && token.startsWith(query.toLowerCase())) {
                    suggestions.add({
                        text: token,
                        type: 'completion',
                        weight: 1
                    });
                }
            }
        }
        
        return Array.from(suggestions)
            .sort((a, b) => b.weight - a.weight)
            .slice(0, this.config.maxSuggestions);
    }
};

// Auto-initialize when imported
AdvancedSearchEngine.initialize(); 